import type { EntityContentLayoutProps } from '@backstage/plugin-catalog-react/alpha';

import {
  coreExtensionData,
  createExtensionInput,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { Icon } from '@iconify/react';
import { lazy as reactLazy } from 'react';

import { buildFilterFn } from './filter/FilterWrapper';

/**
 * @deprecated TODO can be removed after https://github.com/backstage/backstage/pull/31128 is released
 */
export const entityContents = EntityContentBlueprint.makeWithOverrides({
  factory: (originalFactory, { inputs, node }) =>
    originalFactory({
      group: 'overview',
      // TODO this line was added, the whole file can be removed after https://github.com/backstage/backstage/pull/31117 is released
      icon: <Icon icon="material-symbols:info-outline" />,
      loader: async () => {
        const LazyDefaultLayoutComponent = reactLazy(() =>
          import('./DefaultEntityContentLayout').then(m => ({
            default: m.DefaultEntityContentLayout,
          })),
        );

        const DefaultLayoutComponent = (props: EntityContentLayoutProps) => (
          <ExtensionBoundary node={node}>
            <LazyDefaultLayoutComponent {...props} />
          </ExtensionBoundary>
        );

        const layouts = [
          ...inputs.layouts.map(layout => ({
            Component: layout.get(
              EntityContentLayoutBlueprint.dataRefs.component,
            ),
            filter: buildFilterFn(
              layout.get(EntityContentLayoutBlueprint.dataRefs.filterFunction),
              layout.get(
                EntityContentLayoutBlueprint.dataRefs.filterExpression,
              ),
            ),
          })),
          {
            Component: DefaultLayoutComponent,
            filter: buildFilterFn(),
          },
        ];

        const cards = inputs.cards.map(card => ({
          element: card.get(coreExtensionData.reactElement),
          filter: buildFilterFn(
            card.get(EntityContentBlueprint.dataRefs.filterFunction),
            card.get(EntityContentBlueprint.dataRefs.filterExpression),
          ),
          type: card.get(EntityCardBlueprint.dataRefs.type),
        }));

        const Component = () => {
          const { entity } = useEntity();

          // Use the first layout that matches the entity filter
          const layout = layouts.find(l => l.filter(entity));
          if (!layout) {
            throw new Error('No layout found for entity'); // Shouldn't be able to happen
          }

          return (
            <layout.Component
              cards={cards.filter(card => card.filter(entity))}
            />
          );
        };

        return <Component />;
      },
      path: '/',
      title: 'Overview',
    }),
  inputs: {
    cards: createExtensionInput([
      coreExtensionData.reactElement,
      EntityContentBlueprint.dataRefs.filterFunction.optional(),
      EntityContentBlueprint.dataRefs.filterExpression.optional(),
      EntityCardBlueprint.dataRefs.type.optional(),
    ]),
    layouts: createExtensionInput([
      EntityContentLayoutBlueprint.dataRefs.filterFunction.optional(),
      EntityContentLayoutBlueprint.dataRefs.filterExpression.optional(),
      EntityContentLayoutBlueprint.dataRefs.component,
    ]),
  },
  name: 'overview',
});
